import {
  GetSalaryStructureResponse,
  SalaryStructure,
  SalaryStructureComponent,
  SalaryStructureComponentValueType,
} from 'components/SalaryStructure/types';
import { statusFiltersMap } from './constants';
import { SalaryStructureComponentValueTypeLabels } from './creation';
import { EmployeeData } from 'api/gql-utils/user/types';

/**
 * Filters salary structures based on search query or status.
 *
 * @param structures - Array of SalaryStructure to filter.
 * @param filter - Filter object containing search and/or status.
 * @returns Filtered array of SalaryStructure.
 */
export const filterSalaryStructures = (
  structures: SalaryStructure[],
  filter: { search?: string; status?: string },
): SalaryStructure[] => {
  return structures.filter((structure) => {
    const matchesSearch =
      !filter.search || structure.name.toLowerCase().includes(filter.search.toLowerCase());

    const matchesStatus =
      !filter.status ||
      statusFiltersMap[filter.status] === structure.status ||
      filter.status === 'all';

    return matchesSearch && matchesStatus;
  });
};

const formatFormulaValue = (value: string | undefined): string => {
  if (!value) {
    return '';
  }
  const numericValue = parseFloat(value);
  return numericValue % 1 === 0 ? numericValue.toFixed(0) : numericValue.toFixed(2);
};

export const getSalaryStructureComponentFormulas = (component: SalaryStructureComponent) => {
  if (component.salaryComponent.componentType === 'COMPONENT_TYPE_SPECIAL_ALLOWANCE') {
    return 'Residual Value';
  } else if (component.valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_CTC') {
    return `${formatFormulaValue(component.formula)}% of CTC`;
  } else if (component.valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA') {
    return `${formatFormulaValue(component.formula)}% of Special Allowance`;
  } else if (component.valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_BASIC') {
    return `${formatFormulaValue(component.formula)}% of Basic`;
  } else if (component.valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED') {
    return formatFormulaValue(component.formula);
  } else {
    return formatFormulaValue(component.formula);
  }
};

export const getToggleModalContent = (salaryStructure: GetSalaryStructureResponse) => {
  const status = salaryStructure?.status;
  const isActive = status === 'SALARY_STRUCTURE_STATUS_ACTIVE';

  const data = {
    title: '',
    description: '',
  };

  if (isActive) {
    data['title'] = `Disable ${salaryStructure?.name} ?`;
    data['description'] =
      'Once disabled, it cannot be assigned to any employee of your organisation';
  } else {
    data['title'] = `Enable ${salaryStructure?.name} ?`;
    data['description'] = 'Once enabled, you can use the structure to assign it to any employees';
  }

  return data;
};

export const getCalculations = (salaryComponent: SalaryStructureComponent) => {
  const valueType = salaryComponent.valueType as SalaryStructureComponentValueType;
  if (valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA') return 'Residual Amount';
  const suffix = SalaryStructureComponentValueTypeLabels[valueType]?.['label'];
  return `${salaryComponent.formula} ${suffix}`;
};

export const getPrefixedEmployee = (employee: EmployeeData) => {
  if (!employee?.employeeId) return '--';
  if (employee?.idPrefix !== null && employee?.idPrefix !== undefined) {
    return `${employee.idPrefix}${employee.employeeId}`;
  }
  return employee?.employeeId;
};
